<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { adminAPI } from '../service/api.js';

const props = defineProps({
  visible: { type: Boolean, required: true },
  client: { type: Object, default: null }, // {client_id, tenant, description}
});
const emit = defineEmits(['update:visible', 'rotated']);

const toast = useToast();

const submitting = ref(false);
const result = ref(null);
const copiedId = ref(false);
const copiedSecret = ref(false);

const reset = () => {
  submitting.value = false;
  result.value = null;
  copiedId.value = false;
  copiedSecret.value = false;
};

watch(() => props.visible, (now) => {
  if (now) reset();
});

const submit = async () => {
  if (!props.client?.client_id) return;
  submitting.value = true;
  try {
    const response = await adminAPI.rotateApiClient(props.client.client_id);
    result.value = response.data;
    emit('rotated');
  } catch (e) {
    console.error('rotateApiClient failed:', e);
    toast.add({
      severity: 'error',
      summary: 'Failed to rotate client',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    submitting.value = false;
  }
};

const copy = async (text, kind) => {
  try {
    await navigator.clipboard.writeText(text);
    if (kind === 'id') copiedId.value = true;
    if (kind === 'secret') copiedSecret.value = true;
    toast.add({ severity: 'success', summary: 'Copied', life: 1500 });
  } catch (e) {
    toast.add({
      severity: 'warn',
      summary: 'Copy failed — select and copy manually',
      life: 3000,
    });
  }
};

const close = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    :style="{ width: '32rem' }"
    :header="result ? 'Save the new credentials' : 'Rotate API client'"
  >
    <!-- PHASE 1: confirmation -->
    <div v-if="!result" class="form-body">
      <Message severity="warn" :closable="false">
        Rotating issues new credentials and <strong>immediately deactivates</strong>
        the current ones. The partner must switch to the new client right away.
        There is no grace period.
      </Message>

      <div class="summary">
        <div><strong>Tenant:</strong> {{ client?.tenant }}</div>
        <div><strong>Current client_id:</strong> <span class="mono">{{ client?.client_id }}</span></div>
        <div v-if="client?.description">
          <strong>Description:</strong> {{ client.description }}
          <span class="help">(inherited by new client)</span>
        </div>
      </div>

      <div class="dialog-actions">
        <Button label="Cancel" severity="secondary" text @click="close" />
        <Button
          label="Rotate now"
          icon="pi pi-refresh"
          severity="warn"
          :loading="submitting"
          @click="submit"
        />
      </div>
    </div>

    <!-- PHASE 2: one-shot reveal of new credentials -->
    <div v-else class="reveal-body">
      <Message severity="warn" :closable="false" class="warn-banner">
        The new client secret is shown <strong>only once</strong>. Copy it now —
        the old credentials are already deactivated.
      </Message>

      <div class="cred-row">
        <label>Tenant</label>
        <div class="cred-value">{{ result.tenant }}</div>
      </div>

      <div class="cred-row">
        <label>New Client ID</label>
        <div class="cred-value mono">
          <span class="cred-text">{{ result.client_id }}</span>
          <Button
            :icon="copiedId ? 'pi pi-check' : 'pi pi-copy'"
            severity="secondary"
            text
            size="small"
            @click="copy(result.client_id, 'id')"
            aria-label="Copy client_id"
          />
        </div>
      </div>

      <div class="cred-row">
        <label>New Client Secret</label>
        <div class="cred-value mono secret">
          <span class="cred-text">{{ result.client_secret }}</span>
          <Button
            :icon="copiedSecret ? 'pi pi-check' : 'pi pi-copy'"
            severity="secondary"
            text
            size="small"
            @click="copy(result.client_secret, 'secret')"
            aria-label="Copy client_secret"
          />
        </div>
      </div>

      <div class="dialog-actions">
        <Button
          label="I've copied these credentials"
          icon="pi pi-check"
          :disabled="!copiedSecret"
          @click="close"
        />
      </div>
      <small v-if="!copiedSecret" class="help">
        The button enables after you click the secret's copy icon.
      </small>
    </div>
  </Dialog>
</template>

<style scoped>
.form-body, .reveal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.summary {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 4px;
}
.help {
  color: #777;
  font-size: 0.75rem;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.warn-banner {
  margin: 0;
}
.cred-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}
.cred-row label {
  font-weight: 500;
  color: #444;
  font-size: 0.875rem;
}
.cred-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.5rem;
}
.cred-text {
  flex: 1;
  word-break: break-all;
}
.mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
}
.secret {
  background: #fef9c3;
  border-color: #fde047;
}
</style>
